import { Icon } from "@iconify/react";
import { translateSubscriptionFeature, useApp, useTranslation } from "@next-platform/client-common-core";
import { BillingCycle } from "@next-platform/client-common-core/user/types";
import { Modal } from "@next-platform/client-common-spicy-ui";
import { Button } from "@nextui-org/react";
import { Key } from "react";

import { Col, Row } from "@/components/customComponents";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";

import { ModalConfirmCancelProps } from "../types";

const ModalConfirmCancel = ({
  isOpen,
  onOpenChange,
  role,
  recurringDate,
  onCancelConfirm,
  isActioning,
  purchaseOptions,
  billingCycle,
}: ModalConfirmCancelProps) => {
  const { t } = useTranslation();
  const { branding } = useApp();
  const plansByCycle: Record<BillingCycle, { role: string; features: string[] }[] | undefined> = {
    [BillingCycle.MONTHLY]: purchaseOptions?.monthly_plans,
    [BillingCycle.QUARTERLY]: purchaseOptions?.quarterly_plans,
    [BillingCycle.ANNUAL]: purchaseOptions?.annual_plans,
  };
  const selectedPlans = plansByCycle[billingCycle ?? BillingCycle.MONTHLY] ?? [];
  const features = (selectedPlans.find((plan) => plan.role === role)?.features ?? []).map((feature) => translateSubscriptionFeature({ feature, t }));
  return (
    <Modal
      title={t("subscription:portal.modalConfirmCancel.title")}
      classNames={{ title: "text-center pl-[28px]" }}
      open={isOpen}
      onOpenChange={onOpenChange}
      backdrop="blur"
    >
      <Col className="gap-3">
        <p className="text-medium text-default-500">
          <Trans i18nKey="subscription:portal.modalConfirmCancel.description" values={{ date: recurringDate }} />
        </p>
        {role ? (
          <ul>
            {features?.map((benefit: string, index: Key) => (
              <li className="flex items-center gap-1" key={index}>
                <Icon className="text-default-600" icon="ci:check" width={24} />

                <p className="text-default-500">{benefit}</p>
              </li>
            ))}
          </ul>
        ) : null}
        <p className="text-medium text-default-500">
          <Trans
            i18nKey="subscription:portal.modalConfirmCancel.needHelp"
            components={{
              contactLink: <LocalizedLink className="underline text-default-700" to={branding?.customerSupportUrl ?? ""} aria-label="contact" />,
            }}
          />
        </p>
      </Col>
      <Row justify="end" className="gap-2 sticky bottom-0">
        <Button color="danger" variant="light" onPress={onCancelConfirm} isDisabled={isActioning} isLoading={isActioning} aria-label="cancel">
          {isActioning ? (
            <span>
              <Trans i18nKey="subscription:portal.modalConfirmCancel.cancelling" />
            </span>
          ) : (
            <span>
              <Trans i18nKey="subscription:portal.modalConfirmCancel.confirmCancellation" />
            </span>
          )}
        </Button>
        <Button color="primary" onPress={() => onOpenChange?.(false)} aria-label="keep-benefits">
          <Trans i18nKey="subscription:portal.modalConfirmCancel.keepBenefits" />
        </Button>
      </Row>
    </Modal>
  );
};

export default ModalConfirmCancel;
